import { css, SerializedStyles } from '@emotion/react';
import { useBreakpoint } from '@frontend/responsiveness';
import { Button, Heading, SpinningLoader, Text } from '@frontend/design-system';

const styleSheets = {
  container: css`
    background-color: var(--fabric-color-neutral-00);
    border: 1px solid var(--fabric-color-seaweed-50);
    border-radius: var(--fabric-geometry-compact-radius-lg);
    position: relative;
    box-shadow: var(--fabric-tooltip-shadow);
  `,
  grid: css`
    display: grid;
    grid-template-columns: 65% 35%;
  `,
  inlineGapXs: css`
    display: flex;
    gap: var(--fabric-space-padding-xs);
    flex-wrap: wrap;
    align-items: center;
  `,
  paddingMd: css`
    padding: var(--fabric-space-padding-md);
  `,
  bgSeawood: css`
    background-color: var(--fabric-color-seaweed-alpha-50-alpha-10);
  `,
  black85: css`
    color: var(--fabric-color-neutral-alpha-100-alpha-85);
  `,
  marginMd: css`
    margin: var(--fabric-space-padding-md);
  `,
  tertiaryButton: css`
    color: var(--fabric-color-neutral-alpha-100-alpha-60);
    &:hover {
      background-color: var(--fabric-color-neutral-10);
      color: var(--fabric-color-neutral-alpha-100-alpha-80);
    }
  `,
  featureImage: css`
    border: 1px solid var(--fabric-color-neutral-alpha-100-alpha-10);
    border-radius: var(--fabric-geometry-compact-radius-lg);
    filter: drop-shadow(0px 4px 8px rgba(30, 50, 81, 0.15)) drop-shadow(0px 1px 3px rgba(30, 50, 81, 0.15));
    width: 260px;
    height: auto;
  `,
  positionAbs: css`
    position: absolute;
    right: 4%;
    top: 8%;
  `,
};

type PaymentsPromoBannerProps = {
  headerText: string;
  HeaderPrefix?: React.FC;
  titleText: string;
  descriptionText: string;
  FeatureImage?: React.FC;
  styleOnContainer?: SerializedStyles;
  footerActions: {
    primary: {
      text: string;
      onClick: () => void;
      trackingId?: string;
    };
    secondary?: {
      text: string;
      onClick: () => void;
      trackingId?: string;
    };
    tertiary?: {
      text: string;
      onClick: () => void;
      trackingId?: string;
    };
  };
  showLoader?: boolean;
};

export const PaymentsPromoBanner = ({
  headerText,
  HeaderPrefix,
  titleText,
  descriptionText,
  FeatureImage,
  footerActions,
  showLoader,
  styleOnContainer,
}: PaymentsPromoBannerProps) => {
  const breakpoint = useBreakpoint();
  return (
    <div css={[styleSheets.container, breakpoint === 'xlarge' && styleSheets.grid, styleOnContainer]}>
      <div css={[styleSheets.inlineGapXs, styleSheets.paddingMd, styleSheets.bgSeawood]}>
        {HeaderPrefix && <HeaderPrefix />}
        <Heading level={3}>{headerText}</Heading>
      </div>
      <div css={styleSheets.bgSeawood} /> {/* Empty div for grid layout support */}
      <div>
        <Text size='large' css={[styleSheets.black85, styleSheets.marginMd]} weight='bold'>
          {titleText}
        </Text>
        <Text size='medium' css={[styleSheets.black85, styleSheets.marginMd]}>
          {descriptionText}
        </Text>
        <div css={[styleSheets.marginMd, styleSheets.inlineGapXs]}>
          {footerActions.primary && (
            <Button
              variant='primary'
              onClick={footerActions.primary.onClick}
              trackingId={footerActions.primary.trackingId}
              disabled={showLoader}
            >
              {footerActions.primary.text}
              {showLoader && <SpinningLoader color='white' css={{ margin: 0 }} size='xs' />}
            </Button>
          )}
          {footerActions.secondary && (
            <Button
              variant='secondary'
              onClick={footerActions.secondary.onClick}
              trackingId={footerActions.secondary.trackingId}
              disabled={showLoader}
            >
              {footerActions.secondary.text}
            </Button>
          )}
          {footerActions.tertiary && (
            <Button
              variant='tertiary'
              onClick={footerActions.tertiary.onClick}
              trackingId={footerActions.tertiary.trackingId}
              css={styleSheets.tertiaryButton}
              disabled={showLoader}
            >
              {footerActions.tertiary.text}
            </Button>
          )}
        </div>
      </div>
      <div>
        {breakpoint === 'xlarge' && FeatureImage && (
          <FeatureImage css={[styleSheets.featureImage, styleSheets.positionAbs]} />
        )}
      </div>
    </div>
  );
};
